import { useEffect, useState, useRef, Fragment } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, MapPin, Check, ShieldCheck, MessageSquare, Flame, BadgeCheck, User, ExternalLink, Heart, CreditCard, Banknote, ChevronDown, Users, Copy } from "lucide-react";
import { toast } from "sonner";
import { formatHour, formatPrice } from "@/lib/utils";
import { getImageUrl } from "@/lib/images";
import DatePicker from "@/components/DatePicker";
import BackButton from "@/components/BackButton";
import { useFavorites } from "@/hooks/useFavorites";

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isPastSlot(hour, dateStr) {
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (dateStr < today) return true;
  if (dateStr > today) return false;
  const currentHour = d.getHours() + d.getMinutes() / 60;
  return hour < currentHour;
}

export default function BookingFlow() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { favoriteIds, toggle } = useFavorites();
  const [court, setCourt] = useState(null);
  const initialDate = searchParams.get("date") || todayStr();
  const [date, setDate] = useState(initialDate);
  const [slots, setSlots] = useState([]);
  const [pickedHours, setPickedHours] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const initialDuration = searchParams.get("duration");
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [bookingStep, setBookingStep] = useState(1);
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [cardForm, setCardForm] = useState({ number: "", expiry: "", cvc: "", name: "" });
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [uploadingProof, setUploadingProof] = useState(false);
  const [cliqProofUrl, setCliqProofUrl] = useState("");
  const bookingStart = searchParams.get("start");
  const bookingEnd = searchParams.get("end");
  const rebookApplied = useRef(false);
  const initialPrefill = useRef(false);

  const loadAvailability = (currentCourt, currentDate, duration) => {
    if (!currentCourt) return;
    setSlotsLoading(true);
    setSlotsError("");
    const dur = duration || currentCourt.default_slot_duration || currentCourt.slot_duration || 60;
    api
      .get(`/courts/${currentCourt.id}/availability`, { params: { date: currentDate, duration: dur } })
      .then((r) => {
        const now = new Date();
        const today = todayStr();
        const currentHour = now.getHours() + now.getMinutes() / 60;
        const slots = (r.data.slots || []).map((s) => {
          if (currentDate < today) {
            return { ...s, available: false };
          }
          if (currentDate === today && s.hour < currentHour) {
            return { ...s, available: false };
          }
          return s;
        });
        setSlots(slots);
      })
      .catch((e) => {
        setSlots([]);
        setSlotsError(formatApiError(e.response?.data?.detail) || t("court.no_slots"));
        if (import.meta.env.DEV) console.warn("availability load failed:", e);
      })
      .finally(() => setSlotsLoading(false));
  };

  useEffect(() => {
    api.get(`/courts/${id}`).then((r) => {
      setCourt(r.data);
      const dur = initialDuration ? parseInt(initialDuration) : (r.data.default_slot_duration || r.data.slot_duration || 60);
      setSelectedDuration(dur);
      const accepted = r.data.accepted_payment_methods || ['cod'];
      setPaymentMethod(accepted[0]);
      loadAvailability(r.data, date, dur);
    });
    api.get('/teams').then((r) => setAvailableTeams(r.data.teams || [])).catch((e) => {
      if (import.meta.env.DEV) console.warn("teams load failed:", e);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (court) {
      setPickedHours([]);
      setSelectedExtras([]);
      if (!initialPrefill.current) {
        setBookingStep(1);
      }
      loadAvailability(court, date, selectedDuration);
      const accepted = court.accepted_payment_methods || ['online'];
      if (!accepted.includes(paymentMethod)) {
        setPaymentMethod(accepted[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    if (!bookingStart || !bookingEnd || rebookApplied.current || slots.length === 0) return;
    const step = (selectedDuration || court?.slot_duration || 60) / 60;
    const hours = [];
    for (let h = parseFloat(bookingStart); h < parseFloat(bookingEnd); h = Math.round((h + step) * 100) / 100) {
      hours.push(h);
    }
    if (hours.length > 0) {
      setPickedHours(hours);
      rebookApplied.current = true;
      initialPrefill.current = true;
      const hasExtras = court.extras && court.extras.length > 0;
      setBookingStep(hasExtras ? 2 : 3);
    }
  }, [slots, bookingStart, bookingEnd, court]);

  const toggleHour = (h, available) => {
    if (!available || isPastSlot(h, date)) return;
    const step = (selectedDuration || court?.slot_duration || 60) / 60;
    setPickedHours((prev) => {
      if (prev.includes(h)) return prev.filter((x) => x !== h);
      const next = [...prev, h].sort((a, b) => a - b);
      for (let i = 1; i < next.length; i++) {
        if (Math.abs(next[i] - next[i - 1] - step) > 0.001) return [h];
      }
      return next;
    });
  };

  const extrasTotal = selectedExtras.reduce((sum, e) => sum + e.price * (e.qty || 1), 0);
  const pickedSlots = slots.filter((s) => pickedHours.includes(s.hour));
  const total = pickedSlots.reduce((sum, s) => sum + (s.price || 0), 0) + extrasTotal;
  const originalSlotTotal = pickedSlots.reduce((sum, s) => sum + (s.original_price || s.price || 0), 0);
  const autoDiscountAmount = originalSlotTotal - (total - extrasTotal);
  const autoDiscountCode = pickedSlots.find((s) => s.discount_code)?.discount_code || "";
  const autoDiscountLabel = pickedSlots.find((s) => s.discount_label)?.discount_label || "";
  const autoDiscountDisplay = autoDiscountCode || autoDiscountLabel || t("court.discount");
  const originalTotal = originalSlotTotal + extrasTotal;
  const effectiveTotal = total - (promoDiscount?.amount || 0);
  const applyPromo = async () => {
    if (!promoCode.trim() || pickedHours.length === 0) return;
    setPromoChecking(true);
    try {
      const step = (court?.slot_duration || 60) / 60;
      const r = await api.post(`/courts/${id}/validate-promo`, {
        code: promoCode.trim(),
        date,
        start_hour: pickedHours[0],
        end_hour: pickedHours[pickedHours.length - 1] + step,
      });
      setPromoDiscount(r.data);
      toast.success(t("court.promo_applied"));
    } catch (e) {
      setPromoDiscount(null);
      toast.error(formatApiError(e.response?.data?.detail) || t("court.promo_invalid"));
    } finally {
      setPromoChecking(false);
    }
  };

  const handleCliqProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("booking.cliq_proof_too_large"));
      return;
    }

    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const proofUrl = uploadRes.data.url;
      setCliqProofUrl(proofUrl);
      toast.success(t("booking.cliq_proof_uploaded"));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("booking.cliq_upload_failed"));
    } finally {
      setUploadingProof(false);
    }
  };

  const book = async () => {
    if (!user) {
      toast.error(t("common.sign_in_to_book"));
      navigate(`/login?redirect=/courts/${id}/book`);
      return;
    }
    if (pickedHours.length === 0) {
      toast.error(t("common.pick_slot"));
      return;
    }
    setSubmitting(true);
    try {
      const step = (selectedDuration || court?.slot_duration || 60) / 60;
      const payload = {
        court_id: id,
        date,
        start_hour: pickedHours[0],
        end_hour: pickedHours[pickedHours.length - 1] + step,
        payment_method: paymentMethod,
        extras: selectedExtras.map((e) => ({ name: e.name, qty: e.qty || 1 })),
      };
      if ((paymentMethod === "cliQ" || paymentMethod === "cliQ_deposit") && cliqProofUrl) {
        payload.cliQ_proof_url = cliqProofUrl;
      }
      if (promoDiscount) {
        payload.discount_code = promoCode.trim();
      }
      const r = await api.post("/bookings", payload);
      const bookingId = r.data.booking?.id || r.data.id;
      // Associate team with booking if selected
      if (selectedTeamId) {
        try {
          await api.post("/team-bookings", { booking_id: bookingId, team_id: selectedTeamId });
        } catch { /* non-critical */ }
      }
      // Submit Cliq proof after booking creation
      if ((paymentMethod === "cliQ" || paymentMethod === "cliQ_deposit") && cliqProofUrl) {
        await api.post(`/bookings/${bookingId}/cliq-proof`, { proof_url: cliqProofUrl });
      }
      toast.success(t("common.booking_confirmed"));
      navigate("/bookings");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const messageHost = async () => {
    if (!user) {
      toast.error(t("common.sign_in_to_chat"));
      navigate(`/login?redirect=/courts/${id}`);
      return;
    }
    try {
      const r = await api.post("/chats", { court_id: id });
      navigate(`/chats?id=${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!court) {
    return <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center"><div className="label-eyebrow animate-pulse">{t("common.loading")}</div></div>;
  }

  const hasPeakHours = (court.peak_hours || []).length > 0 && (court.peak_multiplier || 1) > 1;
  const hasWeekendPeak = (court.weekend_multiplier || 1) > 1;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background overflow-x-hidden">
      <div className="max-w-[1200px] mx-auto px-4 md:px-10 py-8">
        <BackButton className="mb-6" />

        <div className="grid md:grid-cols-[1fr_400px] gap-10 items-start">
          {/* ──── Left: Stepped Booking Flow ──── */}
          <div className="order-2 md:order-1">
            {/* Court summary */}
            <div className="border border-border bg-card overflow-hidden mb-8">
              <div className="flex gap-4 p-4">
                <div className="w-24 h-24 flex-shrink-0 overflow-hidden border border-border">
                  <img src={getImageUrl(court.images?.[0], 'thumbnail')} alt={court.name} className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="label-eyebrow text-volt mb-1">{t("home.sports." + court.sport)}</div>
                      <h1 className="font-heading text-3xl md:text-4xl tracking-tight">{court.name}</h1>
                    </div>
                    <button
                      onClick={() => toggle(court.id)}
                      className="h-8 w-8 flex items-center justify-center border border-border bg-background hover:bg-muted transition-colors rounded-sm flex-shrink-0"
                    >
                      <Heart className={`h-4 w-4 transition-colors ${favoriteIds.includes(court.id) ? "fill-red-500 text-red-500" : "text-muted-foreground hover:text-red-400"}`} />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    {court.rating > 0 && (
                      <span className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-volt text-volt" />
                        {court.rating}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {court.address}
                    </span>
                  </div>
                  <a
                    href={`/courts/${court.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-volt hover:underline mt-2"
                  >
                    <ExternalLink className="h-3 w-3" /> {t("court.view_details")}
                  </a>
                </div>
              </div>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-1 mb-8">
                {[1, 2, 3].map((s) => {
                const hasExtras = court.extras && court.extras.length > 0;
                if (s === 2 && !hasExtras) return null;
                const isActive = hasExtras ? bookingStep === s : (s === 1 ? bookingStep === 1 : bookingStep === 2);
                const isCompleted = hasExtras ? bookingStep > s : (s === 1 ? bookingStep > 1 : bookingStep > 1);
                return (
                  <Fragment key={s}>
                    {s > 1 && (
                      <div className={`flex-1 h-px ${isCompleted || (hasExtras ? bookingStep >= s : bookingStep >= (s === 1 ? 1 : 2)) ? "bg-volt" : "bg-border/20"}`} />
                    )}
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                        isActive ? "bg-volt text-primary-foreground" : isCompleted ? "bg-volt/30 text-volt" : "bg-border/30 text-muted-foreground"
                      }`}>
                        {isCompleted ? <Check className="h-4 w-4" /> : s}
                      </div>
                      <span className={`text-[11px] tracking-widest uppercase font-bold hidden sm:inline ${isActive ? "text-volt" : isCompleted ? "text-volt/70" : "text-muted-foreground"}`}>
                        {s === 1 ? t("court.select_date") : s === 2 ? t("court.extras") : t("court.review")}
                      </span>
                    </div>
                  </Fragment>
                );
              })}
            </div>

            {/* Step 1: Pick date & time */}
            {bookingStep === 1 && (
              <div key={1} className="border border-border bg-card p-6 transition-all duration-300">
                <label className="label-eyebrow block mb-2">{t("court.select_date")}</label>
                <DatePicker value={date} onChange={setDate} min={todayStr()} />

                <label className="label-eyebrow block mt-6 mb-2">
                  {t("court.pick_slots")}
                  {court.slot_duration && court.slot_duration !== 60
                    ? ` (${court.slot_duration} min)`
                    : ""}
                </label>
                {slotsLoading ? (
                  <div className="flex items-center justify-center py-10 text-muted-foreground/70">
                    <div className="label-eyebrow animate-pulse">{t("common.loading")}</div>
                  </div>
                ) : slots.length === 0 ? (
                  <div className="border border-dashed border-border/60 bg-background/40 p-6 text-center text-sm text-muted-foreground/80">
                    <div className="font-semibold text-foreground/80">{t("court.no_slots")}</div>
                    {slotsError && <div className="mt-1 text-xs text-muted-foreground/70">{slotsError}</div>}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-80 overflow-auto">
                    {slots.map((s) => {
                    const picked = pickedHours.includes(s.hour);
                    const discountActive = s.has_discount && s.original_price > s.price;
                    return (
                      <button
                        key={s.hour}
                        type="button"
                        disabled={!s.available}
                        onClick={() => toggleHour(s.hour, s.available)}
                        className={`py-3 text-xs font-mono border transition-all relative ${
                          !s.available
                            ? "border-red-400/20 bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(248,113,113,0.06)_3px,rgba(248,113,113,0.06)_6px)] text-muted-foreground/60 cursor-not-allowed"
                            : picked
                            ? "border-volt bg-volt text-primary-foreground font-bold"
                            : "border-border hover:border-foreground/40"
                        }`}
                      >
                        {formatHour(s.hour)}
                        <div className={`text-[8px] ${picked ? "text-muted-foreground" : "text-muted-foreground"}`}>
                          {discountActive ? (
                            <span>
                              <span className="line-through opacity-50">{formatPrice(s.original_price, court.currency)}</span>{" "}
                              <span className={picked ? "text-primary-foreground" : "text-volt"}>{formatPrice(s.price, court.currency)}</span>
                            </span>
                          ) : (
                            formatPrice(s.price, court.currency)
                          )}
                        </div>
                        {(s.is_peak || (hasWeekendPeak && s.is_weekend)) && (
                          <Flame className="absolute top-0.5 end-0.5 h-2.5 w-2.5 text-volt opacity-80" />
                        )}
                        {(discountActive && s.available) && (
                          <span className="absolute top-0.5 start-0.5 bg-volt text-primary-foreground text-[7px] leading-none px-0.5 py-[1px] font-bold">
                            -{Math.round((1 - s.price / s.original_price) * 100)}%
                          </span>
                        )}
                        {!s.available && (
                          <span className="absolute top-0.5 start-0.5 bg-red-400/80 text-white text-[7px] leading-none px-1 py-[1px] font-bold tracking-wider">
                            {t("court.booked")}
                          </span>
                        )}
                      </button>
                    );
                    })}
                  </div>
                )}

                <button
                  onClick={() => {
                    if (pickedHours.length === 0) {
                      toast.error(t("common.pick_slot"));
                      return;
                    }
                    setBookingStep(court.extras && court.extras.length > 0 ? 2 : 3);
                  }}
                  className="btn-volt w-full mt-6 py-3 text-xs"
                >
                  {t("common.next")}
                </button>
              </div>
            )}

            {/* Step 2: Extras */}
            {bookingStep === 2 && court.extras && court.extras.length > 0 && (
              <div key={2} className="border border-border bg-card p-6 transition-all duration-300">
                <label className="label-eyebrow block mb-4">{t("court.extras")}</label>
                <div className="space-y-3 max-h-96 overflow-auto">
                  {court.extras.map((extra, i) => {
                    const sel = selectedExtras.find((e) => e.name === extra.name);
                    const selIdx = selectedExtras.findIndex((e) => e.name === extra.name);
                    const maxQty = extra.max_qty || 1;
                    const minQty = extra.min_qty || 1;
                    return (
                      <div key={i} className="border border-border/30 bg-background p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!sel}
                            onChange={() => {
                              if (sel) {
                                setSelectedExtras(selectedExtras.filter((e) => e.name !== extra.name));
                              } else {
                                setSelectedExtras([...selectedExtras, { name: extra.name, description: extra.description || '', price: extra.price || 0, qty: minQty }]);
                              }
                            }}
                            className="accent-volt"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">{extra.name}</div>
                            {extra.description && <div className="text-[10px] text-muted-foreground mt-0.5">{extra.description}</div>}
                          </div>
                          {extra.price > 0 && (
                            <div className="text-volt text-xs font-bold flex-shrink-0">
                              +{formatPrice(extra.price, court.currency)}{maxQty > 1 ? ` ${t("court.extras_each")}` : ""}
                            </div>
                          )}
                        </label>
                        {sel && maxQty > 1 && (
                          <div className="flex items-center gap-2 mt-3 ms-10">
                            <button
                              type="button"
                              onClick={() => {
                                const next = [...selectedExtras];
                                next[selIdx] = { ...next[selIdx], qty: Math.max(minQty, (next[selIdx].qty || 1) - 1) };
                                setSelectedExtras(next);
                              }}
                              disabled={sel.qty <= minQty}
                              className="w-8 h-8 flex items-center justify-center border border-border text-sm font-bold hover:border-foreground/40 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              –
                            </button>
                            <span className="text-sm font-mono w-8 text-center">{sel.qty}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const next = [...selectedExtras];
                                next[selIdx] = { ...next[selIdx], qty: Math.min(maxQty, (next[selIdx].qty || 1) + 1) };
                                setSelectedExtras(next);
                              }}
                              disabled={sel.qty >= maxQty}
                              className="w-8 h-8 flex items-center justify-center border border-border text-sm font-bold hover:border-foreground/40 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              +
                            </button>
                            <span className="text-[10px] text-muted-foreground/60 ms-1">{t("court.extras_qty")}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setBookingStep(1)}
                    className="btn-outline rounded-sm flex-1 py-3 text-xs"
                  >
                    {t("common.back")}
                  </button>
                  <button
                    onClick={() => setBookingStep(3)}
                    className="btn-volt flex-1 py-3 text-xs"
                  >
                    {t("common.next")}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Review & Pay */}
            {bookingStep === 3 && (
              <div key={3} className="border border-border bg-card p-6 transition-all duration-300">
                <h3 className="font-heading text-2xl mb-4">{t("court.review")}</h3>

                <div className="space-y-4">
                  <div className="flex flex-col gap-1 py-3 border-b border-border sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm text-muted-foreground">{t("booking_details.date")}</span>
                    <span className="text-sm font-semibold">{date}</span>
                  </div>
                  <div className="flex flex-col gap-1 py-3 border-b border-border sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm text-muted-foreground">{t("booking_details.time")}</span>
                    <span className="text-sm font-semibold font-mono">
                      {pickedHours.length > 0 && (
                        <>{formatHour(pickedHours[0])} – {formatHour(pickedHours[pickedHours.length - 1] + (selectedDuration || court?.slot_duration || 60) / 60)}</>
                      )}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 py-3 border-b border-border sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm text-muted-foreground">{t("court.slots_count")}</span>
                    <span className="text-sm font-semibold">{pickedHours.length} {t("court.slots_unit")}</span>
                  </div>

                  {selectedExtras.length > 0 && (
                    <div className="py-3 border-b border-border">
                      <span className="text-sm text-muted-foreground block mb-2">{t("court.extras")}</span>
                      {selectedExtras.map((e, i) => (
                        <div key={i} className="flex flex-col gap-1 py-1 text-sm text-foreground/80 sm:flex-row sm:items-center sm:justify-between">
                          <span>{e.name}{e.qty > 1 ? ` x${e.qty}` : ""}</span>
                          {e.price > 0 && <span className="text-volt">+{formatPrice(e.price * (e.qty || 1), court.currency)}</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {availableTeams.length > 0 && (
                    <div className="py-3 border-b border-border">
                      <label className="text-sm text-muted-foreground block mb-3">
                        <Users className="h-3 w-3 inline-block mr-1" />
                        {t("booking_details.team")} <span className="text-muted-foreground/50 text-[10px]">({t("common.optional")})</span>
                      </label>
                      <select
                        value={selectedTeamId}
                        onChange={(e) => setSelectedTeamId(e.target.value)}
                        className="w-full bg-background border border-input px-3 py-2 text-sm focus:border-volt focus:outline-none text-foreground"
                      >
                        <option value="">{t("booking_details.team_none")}</option>
                        {availableTeams.map((team) => (
                          <option key={team.id} value={team.id}>{team.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="py-3">
                    <label className="text-sm text-muted-foreground block mb-3">{t("court.promo_code")}</label>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => { setPromoCode(e.target.value); setPromoDiscount(null); }}
                        placeholder={t("court.promo_placeholder")}
                        className="flex-1 bg-background border border-input px-3 py-2 text-sm focus:border-volt focus:outline-none uppercase"
                      />
                      <button
                        onClick={applyPromo}
                        disabled={promoChecking || !promoCode.trim()}
                        className="btn-outline rounded-sm px-4 py-2 text-[10px] tracking-widest uppercase font-bold disabled:opacity-40"
                      >
                        {promoChecking ? t("common.loading") : t("court.promo_apply")}
                      </button>
                    </div>
                    {promoDiscount && (
                      <div className="text-emerald-400 text-xs mt-2 flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        {promoDiscount.label} ({promoDiscount.type === "percent" ? `${promoDiscount.value}%` : formatPrice(promoDiscount.value, court.currency)} off)
                      </div>
                    )}
                  </div>

                  {autoDiscountAmount > 0.01 && (
                    <div className="bg-volt/5 border border-volt/20 rounded-sm px-4 py-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <Check className="h-4 w-4 text-volt" />
                          <span className="text-sm text-volt font-semibold">{autoDiscountDisplay}</span>
                        </div>
                        <span className="text-sm text-volt font-bold">-{formatPrice(autoDiscountAmount, court.currency)}</span>
                      </div>
                    </div>
                  )}

                  <div className="py-3">
                    <label className="text-sm text-muted-foreground block mb-3">{t("court.payment_method")}</label>

                    {(() => {
                      const methods = [
                        { key: "cod", icon: <Banknote className="h-5 w-5 text-volt shrink-0" />, label: t("bookings.payment_method_cod"), desc: court?.deposit_percent > 0 ? `${court.deposit_percent}% deposit + cash at court` : "Pay cash at the court upon arrival" },
                      ];
                      const accepted = court.accepted_payment_methods || ['cod'];

                      return methods.filter(m => accepted.includes(m.key)).map((method) => {
                        const isSelected = paymentMethod === method.key;
                        return (
                          <div key={method.key} className={`border mb-2 transition-all ${isSelected ? 'border-volt bg-volt/[0.02]' : 'border-border bg-card hover:border-border/60'}`}>
                            <label
                              className="flex items-center gap-3 p-3 cursor-pointer"
                              onClick={() => setPaymentMethod(method.key)}
                            >
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'border-volt' : 'border-border'}`}>
                                {isSelected && <div className="w-2 h-2 rounded-full bg-volt" />}
                              </div>
                              {method.icon}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold">{method.label}</div>
                                <div className="text-[10px] text-muted-foreground truncate">{method.desc}</div>
                              </div>
                              <ChevronDown className={`h-4 w-4 text-muted-foreground/60 transition-transform shrink-0 ${isSelected ? 'rotate-180' : ''}`} />
                            </label>

                            {isSelected && (
                              <div className="px-3 pb-3 pt-0 border-t border-border/30">
                                {method.key === "online" && (
                                  <div className="mt-3 space-y-3">
                                    <div>
                                      <label className="text-[10px] tracking-widest uppercase text-muted-foreground block mb-1">{t("checkout.card_number")}</label>
                                      <input
                                        type="text"
                                        maxLength={19}
                                        placeholder="4111 1111 1111 1111"
                                        value={cardForm.number}
                                        onChange={(e) => {
                                          const v = e.target.value.replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ").slice(0, 19);
                                          setCardForm({ ...cardForm, number: v });
                                        }}
                                        className="w-full bg-background border border-input rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-volt outline-none"
                                      />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="text-[10px] tracking-widest uppercase text-muted-foreground block mb-1">{t("checkout.expiry")}</label>
                                        <input
                                          type="text"
                                          maxLength={5}
                                          placeholder="MM/YY"
                                          value={cardForm.expiry}
                                          onChange={(e) => {
                                            let v = e.target.value.replace(/\D/g, "");
                                            if (v.length >= 2) v = v.slice(0, 2) + "/" + v.slice(2, 4);
                                            setCardForm({ ...cardForm, expiry: v.slice(0, 5) });
                                          }}
                                          className="w-full bg-background border border-input rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-volt outline-none"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-[10px] tracking-widest uppercase text-muted-foreground block mb-1">{t("checkout.cvc")}</label>
                                        <input
                                          type="text"
                                          maxLength={3}
                                          placeholder="123"
                                          value={cardForm.cvc}
                                          onChange={(e) => setCardForm({ ...cardForm, cvc: e.target.value.replace(/\D/g, "").slice(0, 3) })}
                                          className="w-full bg-background border border-input rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-volt outline-none"
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <label className="text-[10px] tracking-widest uppercase text-muted-foreground block mb-1">{t("checkout.cardholder_name")}</label>
                                      <input
                                        type="text"
                                        placeholder="John Doe"
                                        value={cardForm.name}
                                        onChange={(e) => setCardForm({ ...cardForm, name: e.target.value })}
                                        className="w-full bg-background border border-input rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-volt outline-none"
                                      />
                                    </div>
                                  </div>
                                )}

                                {method.key === "cod" && (
                                  <div className="mt-3 text-[10px] text-muted-foreground tracking-wider">
                                    <Check className="h-3 w-3 inline text-emerald-400 mr-1" />
                                    {t("court.cod_confirmation")}
                                  </div>
                                )}

                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}

                  {(() => {
                    const cliQAlias = court?.cliQ_alias || court?.facility?.cliQ_alias;
                    if (!(court?.cliQ_enabled && cliQAlias)) return null;
                    return (
                    <div className={`border mb-2 transition-all ${paymentMethod === 'cliQ' ? 'border-volt bg-volt/[0.02]' : 'border-border bg-card hover:border-border/60'}`}>
                      <label
                        className="flex items-center gap-3 p-3 cursor-pointer"
                        onClick={() => setPaymentMethod('cliQ')}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${paymentMethod === 'cliQ' ? 'border-volt' : 'border-border'}`}>
                          {paymentMethod === 'cliQ' && <div className="w-2 h-2 rounded-full bg-volt" />}
                        </div>
                        <Banknote className="h-5 w-5 text-volt shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold">{t("booking.cliq_pay")}</div>
                          <div className="text-[10px] text-muted-foreground truncate">Pay instantly via CliQ</div>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-muted-foreground/60 transition-transform shrink-0 ${paymentMethod === 'cliQ' ? 'rotate-180' : ''}`} />
                      </label>

                      {paymentMethod === 'cliQ' && (
                        <div className="px-3 pb-3 pt-0 border-t border-border/30">
                          <div className="mt-3 space-y-3">
                            <p className="text-sm text-foreground">{t("booking.cliq_instructions")}</p>

                            <div className="space-y-2">
                              <div className="flex items-center justify-between bg-background border border-border rounded-sm px-3 py-2">
                                <span className="text-xs text-muted-foreground">{t("owner.cliq_alias")}</span>
                                <span className="font-mono text-sm text-volt">{cliQAlias}</span>
                                <button
                                  type="button"
                                  onClick={() => { navigator.clipboard.writeText(cliQAlias); toast.success(t("booking.cliq_alias_copied")); }}
                                  className="text-xs text-volt hover:underline flex items-center gap-1"
                                >
                                  <Copy className="h-3 w-3" />
                                  {t("common.copy")}
                                </button>
                              </div>

                              <div className="flex items-center justify-between bg-background border border-border rounded-sm px-3 py-2">
                                <span className="text-xs text-muted-foreground">{t("booking.amount")}</span>
                                <span className="font-mono text-sm text-volt">{formatPrice(effectiveTotal, court.currency)}</span>
                                <button
                                  type="button"
                                  onClick={() => { navigator.clipboard.writeText(String(effectiveTotal)); toast.success(t("booking.cliq_amount_copied")); }}
                                  className="text-xs text-volt hover:underline flex items-center gap-1"
                                >
                                  <Copy className="h-3 w-3" />
                                  {t("common.copy")}
                                </button>
                              </div>
                            </div>

                            <div className="pt-2">
                              <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-2 block">{t("booking.cliq_upload_proof")}</label>
                              <input
                                type="file"
                                accept="image/jpeg,image/png,application/pdf"
                                onChange={handleCliqProofUpload}
                                disabled={uploadingProof}
                                className="w-full text-xs text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:bg-volt file:text-black hover:file:bg-volt/80 cursor-pointer"
                              />
                              {uploadingProof && <div className="text-[10px] text-muted-foreground mt-1 animate-pulse">{t("common.loading")}</div>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )})()}

                  {/* Cliq for Deposit */}
                  {(() => {
                    const cliQAlias = court?.cliQ_alias || court?.facility?.cliQ_alias;
                    if (!(court?.cliQ_enabled && cliQAlias)) return null;
                    if (!(court?.deposit_percent > 0)) return null;
                    const depositAmount = Math.round(court.price_per_slot * (court.deposit_percent / 100));
                    return (
                    <div className={`border mb-2 transition-all ${paymentMethod === 'cliQ_deposit' ? 'border-volt bg-volt/[0.02]' : 'border-border bg-card hover:border-border/60'}`}>
                      <label
                        className="flex items-center gap-3 p-3 cursor-pointer"
                        onClick={() => setPaymentMethod('cliQ_deposit')}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${paymentMethod === 'cliQ_deposit' ? 'border-volt' : 'border-border'}`}>
                          {paymentMethod === 'cliQ_deposit' && <div className="w-2 h-2 rounded-full bg-volt" />}
                        </div>
                        <Banknote className="h-5 w-5 text-volt shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold">{t("booking.cliq_pay_deposit")}</div>
                          <div className="text-[10px] text-muted-foreground truncate">Pay {depositAmount} JD deposit via CliQ, rest at court</div>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-muted-foreground/60 transition-transform shrink-0 ${paymentMethod === 'cliQ_deposit' ? 'rotate-180' : ''}`} />
                      </label>

                      {paymentMethod === 'cliQ_deposit' && (
                        <div className="px-3 pb-3 pt-0 border-t border-border/30">
                          <div className="mt-3 space-y-3">
                            <p className="text-sm text-foreground">{t("booking.cliq_instructions")}</p>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between bg-background border border-border rounded-sm px-3 py-2">
                                <span className="text-xs text-muted-foreground">{t("owner.cliQ_alias")}</span>
                                <span className="font-mono text-sm text-volt">{cliQAlias}</span>
                                <button type="button" onClick={() => { navigator.clipboard.writeText(cliQAlias); toast.success(t("booking.cliq_alias_copied")); }} className="text-xs text-volt hover:underline flex items-center gap-1">
                                  <Copy className="h-3 w-3" /> {t("common.copy")}
                                </button>
                              </div>
                              <div className="flex items-center justify-between bg-background border border-border rounded-sm px-3 py-2">
                                <span className="text-xs text-muted-foreground">{t("booking.deposit_amount")}</span>
                                <span className="font-mono text-sm text-volt">{formatPrice(depositAmount, court.currency)}</span>
                                <button type="button" onClick={() => { navigator.clipboard.writeText(String(depositAmount)); toast.success(t("booking.cliq_amount_copied")); }} className="text-xs text-volt hover:underline flex items-center gap-1">
                                  <Copy className="h-3 w-3" /> {t("common.copy")}
                                </button>
                              </div>
                            </div>
                            <div className="pt-2">
                              <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-2 block">{t("booking.cliq_upload_proof")}</label>
                              <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={handleCliqProofUpload} disabled={uploadingProof} className="w-full text-xs text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:bg-volt file:text-black hover:file:bg-volt/80 cursor-pointer" />
                              {uploadingProof && <div className="text-[10px] text-muted-foreground mt-1 animate-pulse">{t("common.loading")}</div>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )})()}
                  </div>

                  {promoDiscount && (
                    <div className="bg-emerald-400/5 border border-emerald-400/20 rounded-sm px-4 py-3 mt-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <Check className="h-4 w-4 text-emerald-400" />
                          <span className="text-sm text-emerald-400 font-semibold">{t("court.discount")} ({promoDiscount.label})</span>
                        </div>
                        <span className="text-sm text-emerald-400 font-bold">-{formatPrice(promoDiscount.amount, court.currency)}</span>
                      </div>
                      {promoDiscount.type === "percent" && (
                        <div className="text-xs text-emerald-400/60 mt-1 ms-6">{promoDiscount.value}% {t("owner.discount_off")}</div>
                      )}
                    </div>
                  )}

                   <div className="flex flex-col gap-2 py-3 border-t border-border sm:flex-row sm:items-center sm:justify-between">
                      <span className="label-eyebrow">{t("court.total")}</span>
                      <div className="text-end">
                        {(autoDiscountAmount > 0.01 || promoDiscount) && (
                          <div className="text-xs text-muted-foreground line-through">{formatPrice(originalTotal, court.currency)}</div>
                        )}
                        <span className="font-heading text-3xl text-volt">{formatPrice(effectiveTotal, court.currency)}</span>
                      </div>
                    </div>

                  <p className="text-[10px] text-muted-foreground/60 tracking-widest uppercase text-center py-2">
                    {paymentMethod === "cod" && court?.deposit_percent > 0
                      ? `${court.deposit_percent}% deposit + cash at court`
                      : paymentMethod === "cod"
                      ? "Pay cash at the court upon arrival"
                      : t("court.payment_mocked")}
                  </p>
                </div>

                <div className="flex flex-col gap-3 mt-6 sm:flex-row">
                  <button
                    onClick={() => setBookingStep(court.extras && court.extras.length > 0 ? 2 : 1)}
                    className="btn-outline rounded-sm flex-1 py-3 text-xs"
                  >
                    {t("common.back")}
                  </button>
                  <button
                    disabled={submitting || pickedHours.length === 0}
                    onClick={book}
                    className="btn-volt flex-1 py-3 text-xs"
                  >
                    {submitting ? t("court.booking") : (court.auto_accept !== false ? t("court.confirm_booking") : t("court.request_to_book"))}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ──── Right: Sticky booking summary ──── */}
          <aside className="order-1 md:order-2 md:sticky md:top-20 self-start">
            <div className="border border-border bg-card p-6">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-heading text-5xl text-volt leading-none">{formatPrice((court.slot_pricing || {})[String(selectedDuration)] ?? court.price_per_slot, court.currency)}</span>
                <span className="label-eyebrow">
                  / {selectedDuration && selectedDuration !== 60
                    ? `${selectedDuration}${t("owner.court_form.min_short")}`
                    : t("court.per_hour")}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground tracking-widest uppercase mt-2">
                <ShieldCheck className="h-3 w-3 text-volt" />
                {court.auto_accept ? t("court.instant_confirm") : t("court.host_approval")}
              </div>
              {(hasPeakHours || hasWeekendPeak) && (
                <div className="flex items-center gap-2 text-[10px] text-volt tracking-widest uppercase mt-2 flex-wrap">
                  <Flame className="h-3 w-3" />
                  {hasPeakHours && <span>{t("court.peak_hours")} +{Math.round((court.peak_multiplier - 1) * 100)}%</span>}
                  {hasPeakHours && hasWeekendPeak && <span>·</span>}
                  {hasWeekendPeak && <span>{t("court.weekends")} +{Math.round((court.weekend_multiplier - 1) * 100)}%</span>}
                </div>
              )}

              <div className="my-5 border-t border-border" />

              {pickedHours.length > 0 && (
                <div className="mb-4">
                  <div className="label-eyeblock mb-2">{t("court.selected_slots")}</div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{date}</span>
                    <span className="font-semibold font-mono">
                      {formatHour(pickedHours[0])} – {formatHour(pickedHours[pickedHours.length - 1] + (selectedDuration || court?.slot_duration || 60) / 60)}
                    </span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">{pickedHours.length} {t("court.slots_unit")}</div>
                </div>
              )}

              {selectedExtras.length > 0 && (
                <div className="mb-4 border-t border-border pt-4">
                  <span className="label-eyeblock block mb-2">{t("court.extras")}</span>
                  {selectedExtras.map((e, i) => (
                    <div key={i} className="flex justify-between text-xs text-muted-foreground py-1">
                      <span>{e.name}{e.qty > 1 ? ` x${e.qty}` : ""}</span>
                      {e.price > 0 && <span className="text-volt">+{formatPrice(e.price * (e.qty || 1), court.currency)}</span>}
                    </div>
                  ))}
                </div>
              )}

              {autoDiscountAmount > 0.01 && (
                <div className="bg-volt/5 border border-volt/20 rounded-sm px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-volt" />
                      <span className="text-xs text-volt font-semibold">{autoDiscountDisplay}</span>
                    </div>
                    <span className="text-xs text-volt font-bold">-{formatPrice(autoDiscountAmount, court.currency)}</span>
                  </div>
                </div>
              )}

              {promoDiscount && (
                <div className="bg-emerald-400/5 border border-emerald-400/20 rounded-sm px-3 py-2.5 mt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-emerald-400" />
                      <span className="text-xs text-emerald-400 font-semibold">{promoDiscount.label}</span>
                    </div>
                    <span className="text-xs text-emerald-400 font-bold">-{formatPrice(promoDiscount.amount, court.currency)}</span>
                  </div>
                </div>
              )}

              <div className="border-t border-border pt-4 flex items-center justify-between">
                <span className="label-eyebrow">{t("court.total")}</span>
                <div className="text-end">
                  {(autoDiscountAmount > 0.01 || promoDiscount) && (
                    <div className="text-xs text-muted-foreground line-through">{formatPrice(originalTotal, court.currency)}</div>
                  )}
                  <span className="font-heading text-3xl text-volt">{formatPrice(effectiveTotal, court.currency)}</span>
                </div>
              </div>

              <p className="text-[10px] text-muted-foreground/60 mt-3 tracking-widest uppercase text-center">
                {paymentMethod === "cod" && court?.deposit_percent > 0
                  ? `${court.deposit_percent}% deposit + cash at court`
                  : paymentMethod === "cod"
                  ? "Pay cash at the court upon arrival"
                  : "-"}
              </p>

              {court.owner_name && (
                <div className="mt-4 border-t border-border pt-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-full bg-volt/20 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-volt" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-0.5">{t("court.host")}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold truncate">{court.owner_name}</span>
                        {court.owner_verified ? (
                          <span className="flex items-center gap-1 text-volt text-[10px] tracking-wider uppercase">
                            <BadgeCheck className="h-3.5 w-3.5" />
                            {t("court.verified")}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/60 text-[10px] tracking-wider uppercase">{t("court.unverified")}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <button
                      onClick={messageHost}
                      className="btn-outline rounded-sm w-full py-3 text-xs flex items-center justify-center gap-2"
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                      {t("court.message_host")}
                    </button>
                    {court.whatsapp_number && (
                      <a
                        href={`https://wa.me/${court.whatsapp_number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
                          pickedHours.length > 0
                            ? `Hi! I'm interested in booking ${court.name} on ${date} at ${formatHour(pickedHours[0])} - ${formatHour(pickedHours[pickedHours.length - 1] + (court?.slot_duration || 60) / 60)}.`
                            : `Hi! I'm interested in booking ${court.name}.`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline rounded-sm w-full py-3 text-xs flex items-center justify-center gap-2 hover:border-green-400 hover:text-green-400 transition-colors"
                      >
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        {t("court.whatsapp_host")}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
